// src/pages/MentalPlus/RenameIslandModal.tsx
import * as React from "react";
import { IonModal } from "@ionic/react";
import styled from "styled-components";
import { useIslandVisibility } from "../../components/organisms/ResonanceContext/IslandVisibilityContext";

// If you already have a shared Card component like in your example:
import Card from "../../components/atoms/Card"; // <-- adjust path to your real Card

type Props = {
  islandKey: string | null;
  onClose: () => void;
};

export const RenameIslandModal: React.FC<Props> = ({ islandKey, onClose }) => {
  const { getTitle, setTitle } = useIslandVisibility();

  const [value, setValue] = React.useState("");
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (islandKey) setValue(getTitle(islandKey));
  }, [islandKey, getTitle]);

  React.useEffect(() => {
    if (!islandKey) return;
    // small delay so modal is in DOM before focusing
    const t = window.setTimeout(() => inputRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [islandKey]);

  if (!islandKey) return null;

  const save = () => {
    const trimmed = value.trim();
    if (trimmed) setTitle(islandKey, trimmed);
    onClose();
  };

  return (
    <IonModal
      isOpen={!!islandKey}
      onDidDismiss={onClose}
      backdropDismiss
      // Optional: helps if you want the background to show through a bit
      // cssClass="rename-island-modal"
    >
      <Container>
        <Card>
          <Title>Rename island</Title>

          <Input
            ref={inputRef}
            value={value}
            placeholder="Island name"
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") save();
              if (e.key === "Escape") onClose();
            }}
          />

          <Buttons>
            <PrimaryButton onClick={save}>Save</PrimaryButton>
            <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          </Buttons>
        </Card>
      </Container>
    </IonModal>
  );
};

const Container = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  width: 100%;
  height: 100%;
  padding: 18px;
`;

const Title = styled.div`
  margin: 12px 0 18px 0;
  font-size: 20px;
  font-weight: bold;
  text-align: center;
  color: ${(props) => props.theme.primary.main};
  padding-bottom: 8px;
  border-bottom: 1px solid ${(props) => props.theme.primary.main};
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 12px;
  border-radius: 8px;
  border: 1px solid rgba(0,0,0,0.18);
  outline: none;
  font-size: 16px;
  margin-top: 6px;

  &:focus {
    border-color: ${(props) => props.theme.primary.main};
    box-shadow: 0 0 0 3px rgba(31, 108, 255, 0.15);
  }
`;

const Buttons = styled.div`
  margin-top: 22px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const PrimaryButton = styled.div`
  background: ${(props) => props.theme.primary.main};
  color: white;
  font-weight: bold;
  padding: 14px 0;
  border-radius: 6px;
  text-align: center;
  cursor: pointer;
`;

const SecondaryButton = styled.div`
  color: ${(props) => props.theme.primary.main};
  font-weight: bold;
  border: 2px solid ${(props) => props.theme.primary.main};
  padding: 14px 0;
  border-radius: 6px;
  text-align: center;
  cursor: pointer;
`;
